import type { ButtonProps, PortalProps } from "@chakra-ui/react";
import { Button, Portal } from "@chakra-ui/react";
import type { CSSProperties, ReactElement, ReactNode } from "react";
import { FiMenu } from "react-icons/fi";
import {
    MenuContent,
    MenuItem,
    MenuItemGroup,
    MenuRoot,
    MenuTrigger,
} from "./components/ui/menu";

export interface MenuOption {
    key: string;
    label: ReactNode;
    onClick?: (
        event?: React.MouseEvent<HTMLDivElement, MouseEvent>,
    ) => Promise<void> | void;
    subMenu?: MenuOption[];
}

export interface MenuButtonComboProps {
    icon?: ReactElement;
    title?: ReactNode;
    options: MenuOption[];
    variant?: ButtonProps["variant"];
    size?: ButtonProps["size"];
    portal?: boolean | PortalProps;
    disabled?: boolean;
    colorPalette?: ButtonProps["colorPalette"];
    styles?: {
        menu?: CSSProperties;
        button?: CSSProperties;
    };
}

const MenuButtonCombo = (p: MenuButtonComboProps): ReactElement => {
    let content = (
        <MenuContent style={p.styles?.menu} portalled={!!p.portal}>
            {p.options.map(({ key, label, onClick, subMenu }) => {
                if (subMenu) {
                    return (
                        <MenuItemGroup key={key} title={label?.toString()}>
                            {subMenu.map(
                                ({
                                    key: key1,
                                    label: label1,
                                    onClick: onClick1,
                                }) => (
                                    <MenuItem
                                        value={key1}
                                        key={key1}
                                        onClick={onClick1}
                                        width="100%"
                                    >
                                        {label1}
                                    </MenuItem>
                                ),
                            )}
                        </MenuItemGroup>
                    );
                }
                return (
                    <MenuItem value={key} key={key} onClick={onClick}>
                        {label}
                    </MenuItem>
                );
            })}
        </MenuContent>
    );
    if (p.portal) {
        const portalProps: PortalProps = p.portal === true ? {} : p.portal;
        content = <Portal {...portalProps}>{content}</Portal>;
    }
    return (
        <MenuRoot>
            <MenuTrigger asChild>
                <Button
                    variant={p.variant ?? "outline"}
                    size={p.size}
                    style={p.styles?.button}
                    onClick={e => {
                        e.stopPropagation();
                    }}
                    disabled={p.disabled}
                    colorPalette={p.colorPalette}
                >
                    {p.icon ?? (!p.title ? <FiMenu /> : undefined)}
                    {p.title}
                </Button>
            </MenuTrigger>
            {content}
        </MenuRoot>
    );
};

export default MenuButtonCombo;
